import React, { useId, useLayoutEffect, useRef } from 'react';
import ControlButton from '../WiwFooter/ControlButton';
import './index.scss';

// Mute sibling branches, never an ancestor of the dialog or selected media.
// This keeps live players in place (no clones, overlays or editor DOM rewrites).
function focusBranches(root, targets) {
  const muted = new Set();
  const paths = new Set();
  const walk = node => {
    if (targets.includes(node)) return;
    if (!targets.some(target => node.contains(target))) { muted.add(node); return; }
    paths.add(node);
    Array.from(node.children).forEach(walk);
  };
  walk(root);
  return { muted, paths };
}

export default function ConfirmationPanel({ question, highlight, className = '', onCancel, busy = false, ...controls }) {
  const panel = useRef(null);
  const latest = useRef({ onCancel, busy });
  latest.current = { onCancel, busy };
  const label = useId();
  useLayoutEffect(() => {
    const dialog = panel.current;
    const root = dialog.closest('#root') || document.body;
    const previousFocus = document.activeElement;
    const touched = new Map();
    let frame;
    const restore = node => {
      const previous = touched.get(node);
      if (!previous) return;
      for (const [name, value] of Object.entries(previous)) {
        if (value === null) node.removeAttribute(name);
        else node.setAttribute(name, value);
      }
      touched.delete(node);
    };
    const mark = (node, values) => {
      if (!touched.has(node)) touched.set(node, Object.fromEntries(
        Object.keys(values).map(name => [name, node.getAttribute(name)])));
      for (const [name, value] of Object.entries(values)) if (node.getAttribute(name) !== value) node.setAttribute(name, value);
    };
    const sync = () => {
      const targets = [dialog, ...(highlight ? Array.from(root.querySelectorAll(highlight)) : [])];
      const { muted, paths } = focusBranches(root, targets);
      const next = new Map();
      paths.forEach(node => next.set(node, { 'data-confirmation-path': '' }));
      muted.forEach(node => {
        // Settings already mute inactive sections. Do not multiply opacity.
        if (!node.closest('[data-inactive="true"]')) next.set(node, { 'data-confirmation-muted': '', inert: '' });
      });
      targets.forEach(node => next.set(node, { 'data-confirmation-focus': '' }));
      for (const [node, previous] of touched) {
        if (!next.has(node) || Object.keys(previous).some(name => !(name in next.get(node)))) restore(node);
      }
      next.forEach((values, node) => mark(node, values));
    };
    sync();
    dialog.querySelector('button:not(:disabled)')?.focus({ preventScroll: true });
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    });
    observer.observe(root, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['data-removing', 'data-removing-image', 'data-inactive'] });
    const outside = event => {
      if (dialog.contains(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.type === 'click' && !latest.current.busy) latest.current.onCancel?.();
    };
    const key = event => {
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopImmediatePropagation();
        if (!latest.current.busy) latest.current.onCancel?.();
      } else if (event.key === 'Tab') {
        const buttons = Array.from(dialog.querySelectorAll('button:not(:disabled)'));
        const current = buttons.indexOf(document.activeElement);
        event.preventDefault();
        buttons[(current + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length]?.focus({ preventScroll: true });
      }
    };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('click', outside, true);
    document.addEventListener('keydown', key, true);
    return () => {
      observer.disconnect(); cancelAnimationFrame(frame);
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('click', outside, true);
      document.removeEventListener('keydown', key, true);
      for (const node of Array.from(touched.keys())) restore(node);
      // Media deletion explicitly returns the caret to the editor. Don't
      // override a focus destination chosen by the action itself.
      if (previousFocus?.isConnected && (document.activeElement === document.body || dialog.contains(document.activeElement))) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [highlight]);

  return <div ref={panel} className={`confirmation-panel ${className}`.trim()} role="alertdialog"
    aria-modal="true" aria-labelledby={label}>
    <p id={label} className="confirmation-question confirmation-panel__question">{question}</p>
    <ControlButton {...controls} busy={busy} disabledCancel={busy} onCancel={onCancel} />
  </div>;
}
